// Core tracking tied to the return statement here.
// We'll add a story to refactor.
/* eslint-disable consistent-return */
import { useStore } from 'react-redux';
import { useUser, useSearchPathBuilder } from 'app/hooks';
import { PathUtils } from '@unisporkal/utilities';
import useSearchMutation from 'app/adapters/shared/ReactQueryReduxAdapter/useSearchMutation';
import useSearchQueryAdapter from 'app/adapters/shared/searchParameters/useSearchQueryAdapter';
import useSearchFactoryAdapter from 'app/adapters/shared/search/useSearchFactoryAdapter';
import { legacyTransition } from 'app/adapters/shared/search/legacyTransition';
import { searchParameterAdapter } from 'app/adapters/shared/searchParameters/searchParametersAdapter';

const shouldLeaveSRP = (gallery, options, searchPathBuilder, queryParams) =>
  gallery.renderGatewayView &&
  !options.preserveGateway &&
  (options.preventGateway ||
    (searchPathBuilder.seoSearchPath(queryParams) || '').length === 0);

const shouldRemainGSRP = (isLeavingGSRP, gallery, options) =>
  !isLeavingGSRP && gallery.renderGatewayView && options.preserveGateway;

const shouldDefaultToImageFilmGrid = (gallery, user) =>
  gallery.searchParameters.assettype === 'image' &&
  gallery.searchParameters.family === 'creative' &&
  user.imageFilmGrid.defaultToImageFilm;

export default () => {
  const store = useStore();
  const toSearchQuery = useSearchQueryAdapter();
  const searchFactory = useSearchFactoryAdapter();
  const searchPathBuilder = useSearchPathBuilder();
  const user = useUser();
  const searchMutation = useSearchMutation();

  /**
   * Every option is read defensively below, so callers pass only the ones they care about.
   * Spelling that out here keeps TypeScript callers from having to restate the defaults.
   *
   * @param {object} [customSearchParameters]
   * @param {{
   *   servicecontext?: string,
   *   preventGateway?: boolean,
   *   preserveGateway?: boolean,
   *   pushState?: boolean,
   * }} [options]
   */
  return (
    customSearchParameters = {},
    options = {
      servicecontext: '',
      preventGateway: false,
      preserveGateway: false,
      pushState: true,
    }
  ) => {
    let searchPath;
    const tempCustomSearchParameters = { ...customSearchParameters };
    // We want an up-to-date copy of gallery.searchParameters.
    // If we were to use the useGallery hook, the searchParameters probably would
    // have been changed by the time the useSearch hook is called.
    const { gallery } = store.getState();
    const searchParameters = searchParameterAdapter(gallery.searchParameters);
    const previousSearchParameters = searchParameterAdapter(
      gallery.completedSearchParameters
    );

    // Re-read on each call: the image/film-grid branch below mutates
    // tempCustomSearchParameters and the path has to be rebuilt from it.
    //
    // Every client-side search passes through here, so it is resolved by the
    // Search aggregate rather than at each caller: a transition from the last
    // completed search, which fills what a new preference scope remembers,
    // resolves its sort, and clears what the new search cannot carry. Redux
    // already holds filter edits made since that search, so the transition is
    // handed the difference as well as this call's own parameters. The raw
    // completed search still goes to the query, which states a few values only
    // when they changed from it. On the router, the completed search becomes
    // the current location.
    const toQueryParams = () => {
      const search = legacyTransition(
        searchFactory,
        previousSearchParameters,
        searchParameters,
        tempCustomSearchParameters
      );

      return toSearchQuery(search.parameters, previousSearchParameters);
    };

    let queryParams = toQueryParams();
    const isLeavingGSRP = shouldLeaveSRP(
      gallery,
      options,
      searchPathBuilder,
      queryParams
    );
    const isRemainingGSRP = shouldRemainGSRP(isLeavingGSRP, gallery, options);

    if (isRemainingGSRP) {
      searchPath = searchPathBuilder.forcedGatewayPath(queryParams);
    } else {
      // Some users eligible for the Image Film Grid experience should be defaulted to the image,film search scope.
      // Since the GSRP doesn't have said scope, we'd like to switch image GSRPs to image,film whenever users
      // leave the GSRP.
      // Film, illustrations and vectors will continue to transition to those same scopes in the
      // internal SRP, though.
      if (isLeavingGSRP && shouldDefaultToImageFilmGrid(gallery, user)) {
        tempCustomSearchParameters.assettype = 'image,film';
      }

      queryParams = toQueryParams();
      searchPath = searchPathBuilder.traditionalSearchPath(queryParams);
    }

    // servicecontext is an internal tracking param - we don't want it on any links we allow robots to crawl,
    // so siteMap won't give us a gateway path under normal conditions when we include it.
    // Here, we are just building a path for an AJAX style request and we want to stay in the Gateway if possible.
    // To accomplish this we build the path without this parameter and THEN append it after the gateway/non-gateway decision has already happened.
    // WARNING: Do not follow this same pattern when building links that will show up as href attributes -
    // the servicecontext parameter is not valid for a gateway path that a robot might crawl.
    if (options.servicecontext) {
      searchPath = PathUtils.appendQueryParamsToString(searchPath, {
        servicecontext: options.servicecontext,
      });
    }

    if (isLeavingGSRP) {
      window.location.href = searchPath;
    } else {
      return searchMutation.mutateAsync({
        searchPath,
        pushState: options.pushState,
      });
    }
  };
};
